/**
 * FaqsSection Component
 *
 * Diamond Architecture: Server Component for static FAQ content.
 *
 * The disclosure list is a native `<details>` block (see
 * `shared/components/FaqDisclosureList`) — the Radix accordion this used to
 * render emitted collapsed items with NO children, so every answer was absent
 * from the server HTML while the `FAQPage` JSON-LD described all of them.
 */
import { FaqDisclosureList } from '@/shared/components/FaqDisclosureList';
import { useTranslations } from '@/shared/i18n';
import { pageFaqs, type PageFaqs } from '@/shared/lib/seo/faq-jsonld';
import type { LocalizedText } from '@letsgethome/types/branded';

// FAQ item keys for iteration — the catalog child names under `home.faqs.items`.
const FAQ_KEYS = ['whatIsLgh', 'propertyTypes', 'hardSell'] as const;
type FaqKey = (typeof FAQ_KEYS)[number];

/**
 * The relative `home.faqs` keys `homePageFaqs` resolves — real members of
 * `MessageKeys<'home.faqs'>` — so the `home.faqs` translator's `.dyn` (typed
 * `DynKey<'home.faqs'>`) is assignable here, exactly like `faqsFromCatalog`'
 * typed translate parameter in `faq-jsonld.ts`.
 */
type HomeFaqTranslate = (
  key: `items.${FaqKey}.question` | `items.${FaqKey}.answer`
) => LocalizedText;

/**
 * The home FAQ paired with its `FAQPage` JSON-LD. The home page emits the
 * JSON-LD alongside the backend `RealEstateAgent` card and hands the SAME value
 * back to {@link FaqsSection}, so the described and the rendered answers are one
 * array (ADR 0142 G4).
 */
export function homePageFaqs(tDyn: HomeFaqTranslate): PageFaqs {
  return pageFaqs(
    FAQ_KEYS.map((key) => ({
      question: tDyn(`items.${key}.question`),
      answer: tDyn(`items.${key}.answer`),
    }))
  );
}

export function FaqsSection({ faqs }: { faqs: PageFaqs }) {
  const t = useTranslations('home.faqs');

  return (
    <section
      id="faqs"
      aria-labelledby="faqs-heading"
      className="bg-background px-4 py-16 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-3xl">
        <h2
          id="faqs-heading"
          className="mb-8 text-center text-3xl font-bold tracking-tight text-foreground sm:text-4xl"
        >
          {t('heading')}
        </h2>

        <FaqDisclosureList faqs={faqs} />
      </div>
    </section>
  );
}
